import { withStyles } from '@material-ui/core/styles';

// ----------------------------------------------------------------------

const GlobalStyles = withStyles((theme) => ({
  '@global': {
    '*': {
      margin: 0,
      padding: 0,
      boxSizing: 'border-box'
    },
    html: {
      width: '100%',
      height: '100%',
      '-ms-text-size-adjust': '100%',
      '-webkit-overflow-scrolling': 'touch',
      ...(theme.palette.appChrome && {
        '--signwise-app-chrome-main': theme.palette.appChrome.main,
        '--signwise-app-chrome-toolbar': theme.palette.appChrome.toolbar,
        '--signwise-app-chrome-gradient-mid': theme.palette.appChrome.gradientMid
      }),
      // Theme-aware CSS variables used by global styles so they update on theme change
      '--sw-text-primary': theme.palette.text.primary,
      '--sw-tab-unselected-bg': 'transparent',
      '--sw-tab-unselected-icon': theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.text.secondary,
      '--sw-density-scale': 1
    },
    body: {
      width: '100%',
      height: '100%',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary
    },
    '#root': {
      width: '100%',
      height: '100%',
      minHeight: '100%',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary
    },
    'html[data-app-density="compact"] body': {
      fontSize: 'calc(16px * var(--sw-density-scale, 0.9))',
      lineHeight: 1.35
    },
    'html[data-app-density="compact"] .MuiTypography-root': {
      fontSize: 'calc(1em * var(--sw-density-scale, 0.9))'
    },
    'html[data-app-density="compact"] .MuiButton-root': {
      minHeight: 'calc(36px * var(--sw-density-scale, 0.9))',
      padding: 'calc(6px * var(--sw-density-scale, 0.9)) calc(14px * var(--sw-density-scale, 0.9))'
    },
    'html[data-app-density="compact"] .MuiTableCell-root': {
      padding: 'calc(10px * var(--sw-density-scale, 0.9))'
    },
    'html[data-app-density="compact"] .MuiToolbar-root': {
      minHeight: 'calc(56px * var(--sw-density-scale, 0.9))'
    },
    
    input: {
      '&[type=number]': {
        MozAppearance: 'textfield',
        '&::-webkit-outer-spin-button': { margin: 0, WebkitAppearance: 'none' },
        '&::-webkit-inner-spin-button': { margin: 0, WebkitAppearance: 'none' }
      }
    },
    textarea: {
      '&::-webkit-input-placeholder': { color: theme.palette.text.disabled },
      '&::-moz-placeholder': { opacity: 1, color: theme.palette.text.disabled },
      '&:-ms-input-placeholder': { color: theme.palette.text.disabled },
      '&::placeholder': { color: theme.palette.text.disabled }
    },
    a: { color: theme.palette.primary.main },
    img: { display: 'block', maxWidth: '100%' },

    // Lazy Load Img
    '.blur-up': {
      WebkitFilter: 'blur(5px)',
      filter: 'blur(5px)',
      transition: 'filter 400ms, -webkit-filter 400ms'
    },
    '.blur-up.lazyloaded ': {
      WebkitFilter: 'blur(0)',
      filter: 'blur(0)'
    }
,

    // Light mode: default Card border + shadow (matches prior OfficialCard styling; dark mode unchanged)
    ...(theme.palette.mode === 'light'
      ? {
          '.MuiCard-root': {
            border: '1px solid',
            borderColor: theme.palette.divider,
            boxShadow: theme.shadows[2]
          }
        }
      : {}),

    // Ensure chart/graph labels and texts follow theme text color
    // Specific to project progress bar component
    '.projectprogressbar, .projectprogressbar *': {
      color: theme.palette.text.primary,
      fill: theme.palette.text.primary
    },

    ...(theme.palette.mode === 'light' && {
      '.MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: `${theme.palette.text.primary} !important`,
        color: `${theme.palette.text.primary} !important`,
        opacity: 1
      }
    }),

    // ApexCharts
    '.apexcharts-text, .apexcharts-xaxis-label, .apexcharts-yaxis-label': {
      color: theme.palette.text.primary,
      fill: theme.palette.text.primary
    },

    // Force y-axis/category labels (ApexCharts) to theme color, override inline fills
    '.apexcharts-yaxis-label, .apexcharts-yaxis-label tspan, .apexcharts-yaxis-label text, .apexcharts-yaxis-label .apexcharts-text, .apexcharts-xaxis-label tspan, .apexcharts-xaxis-label text': {
      fill: `${theme.palette.text.primary} !important`,
      color: `${theme.palette.text.primary} !important`
    },

    // Recharts (SVG text)
    '.recharts-text, .recharts-label, .recharts-legend, .recharts-tooltip': {
      color: theme.palette.text.primary,
      fill: theme.palette.text.primary
    },

    // ECharts / C3 / Chart SVG labels
    '.echarts-label, .c3 text, .ct-label, svg text': {
      color: theme.palette.text.primary,
      fill: theme.palette.text.primary
    },

    // Tabs: force MUI Tab label typography to follow theme text color (fix day mode)
    '.MuiTab-root, .MuiTab-wrapper, .MuiTab-labelIcon, .MuiTab-textColorInherit, .MuiTab-textColorPrimary, .MuiTabs-root, .MuiTabs-flexContainer, .MuiTab-root *:not(.tm-review-tab-badge)': {
      color: `${theme.palette.text.primary} !important`,
      fill: `${theme.palette.text.primary} !important`
    },
    '.MuiTab-root.Mui-selected, .MuiTab-root.Mui-selected *:not(.tm-review-tab-badge)': {
      color: `${theme.palette.text.primary} !important`,
      fill: `${theme.palette.text.primary} !important`
    },
    // Use CSS variables (set on html) so tab styling updates reliably on theme toggle
    '.MuiTabs-root .MuiTab-root:not(.Mui-selected)': {
      backgroundColor: 'var(--sw-tab-unselected-bg) !important',
      color: 'var(--sw-text-primary) !important',
      '& .MuiSvgIcon-root, & svg': {
        color: 'var(--sw-tab-unselected-icon) !important',
        fill: 'var(--sw-tab-unselected-icon) !important'
      }
    },
    '.MuiTabs-root, .MuiTabs-root .MuiTab-root, .MuiTabs-root .MuiTab-root.Mui-selected': {
      backgroundColor: 'transparent !important',
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden'
    },
    // Ensure menu / select options and popover content use theme text/background colors
    '.MuiMenu-paper, .MuiPopover-paper, .MuiMenu-list, .MuiList-root, .MuiMenuItem-root, .MuiSelect-paper, .MuiPaper-root.MuiMenu-paper': {
      color: `${theme.palette.text.primary} !important`,
      backgroundColor: `${theme.palette.background.paper} !important`
    },
    '.MuiMenu-paper:not(.design-job-logs-filter-menu) .MuiMenuItem-root.Mui-selected, .MuiMenu-paper:not(.design-job-logs-filter-menu) .MuiMenuItem-root.Mui-selected *': {
      color: `${theme.palette.text.primary} !important`,
      backgroundColor: `${theme.palette.action.selected} !important`
    },
    '.design-job-logs-filter-menu .MuiMenuItem-root.Mui-selected': {
      backgroundImage: 'none !important',
      border: 'none !important',
      outline: 'none !important',
      boxShadow: 'none !important'
    },
    '.design-job-logs-filter-menu .MuiMenuItem-root.Mui-selected .MuiListItemText-root, .design-job-logs-filter-menu .MuiMenuItem-root.Mui-selected .MuiListItemText-primary': {
      backgroundColor: 'transparent !important',
      border: 'none !important',
      outline: 'none !important',
      boxShadow: 'none !important'
    },

    // App visual zoom: counter-zoom scrollable tabs so MUI scroll buttons/scrollLeft work correctly.
    // MUI v5 alpha uses MuiTabs-scrollableX/Y on the scroller, not MuiTabs-scrollable on root.
    'html[data-app-zoom-below-100] #root .MuiTabs-root:has(.MuiTabs-scrollableX), html[data-app-zoom-below-100] #root .MuiTabs-root:has(.MuiTabs-scrollableY)': {
      zoom: 'calc(1 / var(--sw-app-zoom-scale))',
      transformOrigin: 'top left',
      width: '100%',
      maxWidth: '100%'
    },

    // App visual zoom: keep autocomplete fields and dropdowns aligned with zoomed #root layout.
    'html[data-app-zoom-below-100] .MuiAutocomplete-root .MuiInputBase-root': {
      flexShrink: 0,
      minHeight: 40
    },
    'html[data-app-zoom-below-100] .MuiAutocomplete-popper': {
      boxSizing: 'border-box'
    },
    'html[data-app-zoom-below-100] .MuiAutocomplete-paper, html[data-app-zoom-below-100] .MuiAutocomplete-listbox': {
      boxSizing: 'border-box',
      width: '100%'
    },

    // Ensure chart markers/legend icons use their series colors (don't get forced to text color)
    '.apexcharts-legend .apexcharts-legend-marker, .apexcharts-legend .apexcharts-legend-marker *, .apexcharts-series-markers .apexcharts-marker, .apexcharts-marker, .recharts-legend-item, .recharts-legend-item *': {
      fill: 'initial',
      stroke: 'initial',
      color: 'initial'
    }

    // Tables: ensure table head/cell typography follows the theme
    , 'table, table th, table td, .MuiTable-root, .MuiTableCell-root, .MuiTableCell-body': {
      color: theme.palette.text.primary
    },

    // Force table header text to theme color and override inline styles if necessary
    'table thead th, .MuiTableCell-head, .MuiTableHead-root, .MuiTableHead-root th, .MuiTableSortLabel-root, .MuiTableSortLabel-root *': {
      color: `${theme.palette.text.primary} !important`,
      fill: `${theme.palette.text.primary} !important`
    }
    ,
    // Dialogs: ensure dialog paper, title, content and actions follow the theme
    '.MuiDialog-paper, .MuiDialog-paperScrollPaper, .MuiDialog-paperFullScreen': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.palette.mode === 'light' ? theme.shadows[5] : theme.shadows[8]
    },
    '.MuiDialogTitle-root': {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent'
    },
    '.MuiDialogContent-root, .MuiDialogContentText-root': {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent'
    },
    '.MuiDialogActions-root .MuiButton-root, .MuiDialogActions-root .MuiButton-text': {
      color: theme.palette.text.primary
    },
    // Backdrop should be slightly lighter in light mode, darker in dark mode
    '.MuiBackdrop-root': {
      backgroundColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.32)' : 'rgba(0,0,0,0.6)'
    },
    // Inputs and form controls inside dialogs should follow theme text/background
    '.MuiDialogContent-root input, .MuiDialogContent-root textarea, .MuiDialogContent-root .MuiInputBase-root, .MuiDialogContent-root .MuiTextField-root': {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent'
    },
    // Placeholder color inside dialogs
    '.MuiDialogContent-root ::-webkit-input-placeholder, .MuiDialogContent-root ::placeholder': {
      color: theme.palette.text.disabled
    },

    // Light mode: table pagination — avoid white-on-white (skip Design Job Logs dark card footer)
    ...(theme.palette.mode === 'light'
      ? {
          '.MuiTablePagination-root:not(.design-job-log-table-pagination), .MuiTablePagination-root:not(.design-job-log-table-pagination) .MuiTablePagination-toolbar': {
            color: theme.palette.common.black
          },
          '.MuiTablePagination-root:not(.design-job-log-table-pagination) .MuiTablePagination-selectLabel, .MuiTablePagination-root:not(.design-job-log-table-pagination) .MuiTablePagination-displayedRows': {
            color: `${theme.palette.common.black} !important`
          },
          '.MuiTablePagination-root:not(.design-job-log-table-pagination) .MuiInputBase-root, .MuiTablePagination-root:not(.design-job-log-table-pagination) .MuiSelect-select, .MuiTablePagination-root:not(.design-job-log-table-pagination) .MuiNativeSelect-select': {
            color: `${theme.palette.common.black} !important`,
            WebkitTextFillColor: `${theme.palette.common.black} !important`
          },
          '.MuiTablePagination-root:not(.design-job-log-table-pagination) .MuiSvgIcon-root, .MuiTablePagination-root:not(.design-job-log-table-pagination) .MuiIconButton-root': {
            color: `${theme.palette.common.black} !important`
          }
        }
      : {}),

    // Preserve elements opting out of theme-driven updates (use data-no-theme on elements)
    '[data-no-theme]': {
      color: 'inherit !important',
      backgroundColor: 'inherit !important',
      fill: 'inherit !important',
      transition: 'none !important'
    },

    // Request tab updates card (account): light mode = black body text; dark = theme primary text; job-cost alert stays red
    ...(theme.palette.mode === 'light'
      ? {
          '.sw-request-tab-updates .MuiTypography-subtitle2, .sw-request-tab-updates .MuiTypography-caption': {
            color: `${theme.palette.common.black} !important`
          },
          '.sw-request-tab-updates .MuiTypography-body2:not(.sw-request-tab-updates--jobcost-alert)': {
            color: `${theme.palette.common.black} !important`
          },
          '.sw-request-tab-updates .MuiTypography-body2:not(.sw-request-tab-updates--jobcost-alert) strong': {
            color: `${theme.palette.common.black} !important`
          }
        }
      : {
          '.sw-request-tab-updates .MuiTypography-subtitle2, .sw-request-tab-updates .MuiTypography-caption': {
            color: `${theme.palette.text.primary} !important`
          },
          '.sw-request-tab-updates .MuiTypography-body2:not(.sw-request-tab-updates--jobcost-alert)': {
            color: `${theme.palette.text.primary} !important`
          },
          '.sw-request-tab-updates .MuiTypography-body2:not(.sw-request-tab-updates--jobcost-alert) strong': {
            color: `${theme.palette.text.primary} !important`
          }
        }),
    '.sw-request-tab-updates--jobcost-alert, .sw-request-tab-updates--jobcost-alert strong': {
      color: '#e60303 !important'
    }
  }
}))(() => null);

export default GlobalStyles;
